import { useEffect, useRef } from 'react';

import AircallPhone from 'aircall-everywhere';
import PropTypes from 'prop-types';

import { useAircallEverywhereStore } from '../../stores/aircallEverywhereStore';

import { useCallingMode } from './useAircallEverywhere';

const AIRCALL_DOM_ID = 'aircall-everywhere-root';

/**
 * Renderless provider that manages the Aircall Everywhere SDK lifecycle.
 * Mount once in CrmShell — initializes SDK only when calling mode is 'everywhere'.
 * The hidden DOM target stays in the DOM so the SDK iframe can function even when not visible.
 */
const AircallEverywhereProvider = ({ children }) => {
	const callingMode = useCallingMode();
	const phoneRef = useRef(null);
	const store = useAircallEverywhereStore;

	const setPhoneInstance = store((s) => s.setPhoneInstance);
	const setSdkReady = store((s) => s.setSdkReady);
	const setLoggedIn = store((s) => s.setLoggedIn);
	const handleIncomingCall = store((s) => s.handleIncomingCall);
	const handleOutgoingCall = store((s) => s.handleOutgoingCall);
	const handleOutgoingAnswered = store((s) => s.handleOutgoingAnswered);
	const handleCallEnded = store((s) => s.handleCallEnded);
	const handleCallEndRingtone = store((s) => s.handleCallEndRingtone);
	const reset = store((s) => s.reset);

	useEffect(() => {
		if (callingMode !== 'everywhere') {
			// Cleanup if mode changed away from 'everywhere'
			if (phoneRef.current) {
				phoneRef.current = null;
				reset();
			}
			return;
		}

		// SDK already initialized
		if (phoneRef.current) return;

		const domTarget = document.getElementById(AIRCALL_DOM_ID);
		if (!domTarget) return;

		const phone = new AircallPhone({
			domToLoadWorkspace: `#${AIRCALL_DOM_ID}`,
			onLogin: () => {
				setLoggedIn(true);
				setSdkReady(true);
			},
			onLogout: () => {
				setLoggedIn(false);
			},
		});

		phoneRef.current = phone;
		setPhoneInstance(phone);
		setSdkReady(true);

		// Register SDK event listeners
		phone.on('incoming_call', (callInfos) => handleIncomingCall(callInfos));
		phone.on('outgoing_call', (callInfos) => handleOutgoingCall(callInfos));
		phone.on('outgoing_answered', (callInfos) => handleOutgoingAnswered(callInfos));
		phone.on('call_ended', (callInfos) => handleCallEnded(callInfos));
		phone.on('call_end_ringtone', (callInfos) => handleCallEndRingtone(callInfos));

		return () => {
			phoneRef.current = null;
			reset();
		};
	}, [
		callingMode,
		setPhoneInstance,
		setSdkReady,
		setLoggedIn,
		handleIncomingCall,
		handleOutgoingCall,
		handleOutgoingAnswered,
		handleCallEnded,
		handleCallEndRingtone,
		reset,
	]);

	return children;
};

AircallEverywhereProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AircallEverywhereProvider;
